import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useApiCacheRefresh } from '../lib/apiCache';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Plus, Search, Eye, ChevronRight, Truck } from 'lucide-react';
import { formatDatePL } from '../components/DatePickerField';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    filterProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, searchTerm, statusFilter]);

  useApiCacheRefresh(() => fetchProjects());
  const maskedFinance = (lvl) => projects.some((p) => p.finance_level === lvl);
  const showOffer = user?.role !== 'montaz' && !maskedFinance('none');
  const showMargins = showOffer && !maskedFinance('offer_commission');

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/projects`);
      // Exclude leads, waiting and rejected — they live in /leads now
      setProjects(response.data.filter(p => p.status !== 'lead' && p.status !== 'oczekujacy' && p.status !== 'brak_akceptacji'));
    } catch (error) {
      toast.error('Błąd pobierania zleceń');
    } finally {
      setLoading(false);
    }
  };

  const filterProjects = () => {
    let filtered = projects;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.nazwa?.toLowerCase().includes(term) ||
        p.klient?.toLowerCase().includes(term) ||
        (p.owner_initials && p.owner_initials.toLowerCase().includes(term)) ||
        (p.telefon_klienta && p.telefon_klienta.toLowerCase().includes(term)) ||
        (p.adres_inwestycji && p.adres_inwestycji.toLowerCase().includes(term)) ||
        (p.email_klienta && p.email_klienta.toLowerCase().includes(term))
      );
    }

    setFilteredProjects(filtered);
  };

  const getStatusBadge = (status) => {
    const variants = {
      'lead': { label: 'Lead/do pomiaru', color: 'bg-blue-600 text-[var(--crm-text-primary)]' },
      'akceptacja': { label: 'Akceptacja', color: 'bg-yellow-500 text-black' },
      'brak_akceptacji': { label: 'Brak akceptacji', color: 'bg-gray-500 text-[var(--crm-text-primary)]' },
      'oplacone': { label: 'Opłacone', color: 'bg-emerald-600 text-white' },
      'dostawa': { label: 'Dostawa', color: 'bg-purple-600 text-[var(--crm-text-primary)]' },
      'w_realizacji': { label: 'W realizacji', color: 'bg-orange-500 text-white' },
      'zakonczone': { label: 'Zakończono', color: 'bg-green-700 text-[var(--crm-text-primary)]' },
      // Legacy support
      'do_realizacji': { label: 'Lead/do pomiaru', color: 'bg-blue-600 text-[var(--crm-text-primary)]' },
      'w_toku': { label: 'W realizacji', color: 'bg-orange-500 text-white' },
      'zakończony': { label: 'Zakończono', color: 'bg-green-700 text-[var(--crm-text-primary)]' }
    };
    const variant = variants[status] || { label: status, color: 'bg-slate-500 text-[var(--crm-text-primary)]' };
    return <Badge className={variant.color}>{variant.label}</Badge>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-[var(--crm-text-primary)]">Ładowanie...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-8 lg:p-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-4xl font-manrope font-bold text-[var(--crm-text-primary)] tracking-tight">Zlecenia</h1>
            <p className="text-[var(--crm-text-secondary)] mt-1 text-sm md:text-base">Zarządzaj zleceniami ({projects.length})</p>
          </div>
          {user?.role !== 'guest' && user?.role !== 'montaz' && (
            <Link to="/projects/new">
              <Button data-testid="add-project-button" className="w-full sm:w-auto bg-[#FF6B35] hover:bg-[var(--crm-accent-hover)] text-white">
                <Plus className="w-4 h-4 mr-2" />
                Nowe Zlecenie
              </Button>
            </Link>
          )}
        </div>

        {/* Filters */}
        <div className="bg-[var(--crm-bg-dialog)] border border-[var(--crm-border)] rounded-lg p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[var(--crm-text-secondary)]" />
              <Input
                placeholder="Szukaj: nazwa, klient, telefon, adres..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="search-projects-input"
                className="pl-10 bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="filter-status-select" className="w-full sm:w-48 bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] text-[var(--crm-text-primary)]">
                <SelectItem value="all">Wszystkie</SelectItem>
                <SelectItem value="akceptacja">Akceptacja</SelectItem>
                <SelectItem value="oplacone">Opłacone</SelectItem>
                <SelectItem value="dostawa">Dostawa</SelectItem>
                <SelectItem value="w_realizacji">W realizacji</SelectItem>
                <SelectItem value="zakonczone">Zakończono</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Mobile Cards View */}
        <div className="md:hidden space-y-3">
          {filteredProjects.length === 0 ? (
            <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-8 text-center">
              <p className="text-[var(--crm-text-secondary)]">Brak zleceń do wyświetlenia</p>
            </Card>
          ) : (
            filteredProjects.map((project, index) => (
              <Card
                key={project.id}
                data-testid={`project-card-${project.id}`}
                onClick={() => navigate(`/projects/${project.id}`)}
                className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4 active:bg-white/5 cursor-pointer transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[#FF6B35] font-bold text-lg">#{index + 1}</span>
                      {getStatusBadge(project.status)}
                    </div>
                    <h3 className="text-[var(--crm-text-primary)] font-semibold text-lg truncate">{project.nazwa}</h3>
                    <p className="text-[var(--crm-text-secondary)] text-sm truncate">{project.klient}</p>
                    <p className="text-[var(--crm-text-muted)] text-xs mt-1">
                      {project.created_at ? formatDatePL(project.created_at) : ''}
                    </p>
                    
                    <div className="flex items-center gap-4 mt-3">
                      {project.owner_initials && (
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-[var(--crm-bg-btn-secondary)] flex items-center justify-center">
                            <span className="text-[var(--crm-text-primary)] text-xs font-bold">{project.owner_initials}</span>
                          </div>
                        </div>
                      )}
                      {project.delivery_date && (
                        (() => {
                          const today = new Date(); today.setHours(0,0,0,0);
                          const dd = new Date(project.delivery_date); dd.setHours(0,0,0,0);
                          const diff = Math.ceil((dd - today) / 86400000);
                          const isOverdue = diff < 0 && project.status !== 'w_realizacji' && project.status !== 'zakonczone';
                          const isSoon = diff >= 0 && diff <= 2;
                          return (
                            <span className={`flex items-center gap-1 text-xs ${isOverdue ? 'text-red-400' : isSoon ? 'text-orange-400' : 'text-purple-300'}`}>
                              <Truck className="w-3 h-3" />
                              {formatDatePL(dd)}
                            </span>
                          );
                        })()
                      )}
                      {showMargins && (
                        <div className="flex items-center gap-3 text-sm">
                          <span className="text-[var(--crm-text-secondary)]">Marża:</span>
                          <span className={`font-mono font-bold ${project.margin >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {project.margin.toLocaleString('pl-PL')} zł
                          </span>
                          <span className={`font-mono ${project.margin_percent >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            ({project.margin_percent.toFixed(1)}%)
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-[var(--crm-text-secondary)] flex-shrink-0 mt-2" />
                </div>
              </Card>
            ))
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block bg-[var(--crm-bg-dialog)] border border-[var(--crm-border)] rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-[var(--crm-bg-panel)] border-b border-[var(--crm-border)] hover:bg-[var(--crm-bg-panel)]">
                <TableHead className="text-[var(--crm-text-secondary)] font-bold w-16">Nr</TableHead>
                <TableHead className="text-[var(--crm-text-secondary)] font-bold">Nazwa</TableHead>
                <TableHead className="text-[var(--crm-text-secondary)] font-bold">Klient</TableHead>
                <TableHead className="text-[var(--crm-text-secondary)] font-bold">Właściciel</TableHead>
                <TableHead className="text-[var(--crm-text-secondary)] font-bold">Data utworzenia</TableHead>
                {showOffer && <TableHead className="text-[var(--crm-text-secondary)] font-bold">Oferta</TableHead>}
                {showMargins && (
                  <>
                    <TableHead className="text-[var(--crm-text-secondary)] font-bold">Marża</TableHead>
                    <TableHead className="text-[var(--crm-text-secondary)] font-bold">Marża %</TableHead>
                  </>
                )}
                <TableHead className="text-[var(--crm-text-secondary)] font-bold">Status</TableHead>
                <TableHead className="text-[var(--crm-text-secondary)] font-bold">Data dostawy</TableHead>
                <TableHead className="text-[var(--crm-text-secondary)] font-bold">Data zakończenia</TableHead>
                <TableHead className="text-[var(--crm-text-secondary)] font-bold">Akcje</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredProjects.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={user?.role === 'montaz' ? 8 : 11} className="text-center text-[var(--crm-text-secondary)] py-8">
                    Brak zleceń do wyświetlenia
                  </TableCell>
                </TableRow>
              ) : (
                filteredProjects.map((project, index) => (
                  <TableRow
                    key={project.id}
                    data-testid={`project-row-${project.id}`}
                    className="border-b border-[var(--crm-border)] hover:bg-[var(--crm-bg-card-hover)] transition-colors"
                  >
                    <TableCell className="text-[#FF6B35] font-bold text-lg">
                      {index + 1}
                    </TableCell>
                    <TableCell className="text-[var(--crm-text-primary)] font-medium">
                      <span className="flex items-center gap-2">
                        {project.nazwa}
                        {project.tags?.includes('Zaniedbany') && (
                          <span className="bg-red-500/20 text-red-400 border border-red-500/30 text-[10px] font-semibold px-1.5 py-0.5 rounded" data-testid={`neglected-badge-${project.id}`}>
                            Zaniedbany
                          </span>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="text-[var(--crm-text-secondary)]">{project.klient}</TableCell>
                    <TableCell>
                      {project.owner_initials ? (
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-[var(--crm-bg-btn-secondary)] flex items-center justify-center">
                            <span className="text-[var(--crm-text-primary)] text-xs font-bold">{project.owner_initials}</span>
                          </div>
                        </div>
                      ) : (
                        <span className="text-[var(--crm-text-muted)] text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-[var(--crm-text-secondary)] text-sm">
                      {project.created_at ? formatDatePL(project.created_at) : '—'}
                    </TableCell>
                    {showOffer && (
                        <TableCell className="text-[var(--crm-text-secondary)] font-mono">
                          {project.total_offer.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
                        </TableCell>
                    )}
                    {showMargins && (
                      <>
                        <TableCell className="font-mono">
                          <div className="flex flex-col">
                            <span className={project.net_margin >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                              {(project.net_margin ?? project.margin).toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
                            </span>
                            {project.labor_cost > 0 && (
                              <span className="text-red-400/60 text-xs">-{project.labor_cost.toLocaleString('pl-PL')} rob.</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="font-mono">
                          <span className={(project.net_margin_percent ?? project.margin_percent) >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                            {(project.net_margin_percent ?? project.margin_percent).toFixed(1)}%
                          </span>
                        </TableCell>
                      </>
                    )}
                    <TableCell>{getStatusBadge(project.status)}</TableCell>
                    <TableCell className="text-sm">
                      {project.delivery_date ? (
                        (() => {
                          const today = new Date(); today.setHours(0,0,0,0);
                          const dd = new Date(project.delivery_date); dd.setHours(0,0,0,0);
                          const diff = Math.ceil((dd - today) / 86400000);
                          const isOverdue = diff < 0 && project.status !== 'w_realizacji' && project.status !== 'zakonczone';
                          const isSoon = diff >= 0 && diff <= 2;
                          return (
                            <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-400' : isSoon ? 'text-orange-400' : 'text-purple-300'}`}>
                              <Truck className="w-3.5 h-3.5" />
                              {formatDatePL(dd)}
                            </span>
                          );
                        })()
                      ) : (
                        <span className="text-[var(--crm-text-muted)]">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-[var(--crm-text-secondary)] text-sm">
                      {project.completed_at ? (
                        <span className="text-emerald-400">
                          {formatDatePL(project.completed_at)}
                        </span>
                      ) : (
                        <span className="text-[var(--crm-text-muted)]">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        onClick={() => navigate(`/projects/${project.id}`)}
                        data-testid={`view-project-${project.id}`}
                        className="bg-[var(--crm-bg-btn-secondary)] hover:bg-[var(--crm-bg-card-hover)] text-[var(--crm-text-primary)]"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </Layout>
  );
};
